import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { episodesAPI, evaluationsAPI, sessionNotesAPI, exercisePlansAPI } from "@/lib/api";
import { validate, evaluationSchema, sessionNoteSchema, exercisePlanSchema } from "@/lib/validations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, FileText, ClipboardList, Dumbbell, TrendingUp,
  Plus, Save, CheckCircle, XCircle, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_EVAL = {
  subjective: "", objective: "", assessment: "", plan: "",
  pain_level: 5, rom_notes: "", strength_notes: "",
  functional_notes: "", goals: "", frequency: "",
};

const EMPTY_NOTE = {
  subjective: "", objective: "", interventions: "",
  patient_response: "", progress_notes: "", next_session_plan: "", pain_level: 5,
};

const EMPTY_EXERCISE = { name: "", description: "", sets: 3, reps: 10, frequency: "Diario", hold_seconds: 0, notes: "" };

export default function EpisodeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showExDialog, setShowExDialog] = useState(false);
  const [evalForm, setEvalForm] = useState(EMPTY_EVAL);
  const [noteForm, setNoteForm] = useState(EMPTY_NOTE);
  const [exTitle, setExTitle] = useState("");
  const [exDesc, setExDesc] = useState("");
  const [exercises, setExercises] = useState([{ ...EMPTY_EXERCISE }]);
  const [saving, setSaving] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const [errors, setErrors] = useState({});

  const fetchEpisode = useCallback(async () => {
    try {
      const res = await episodesAPI.get(id);
      setEpisode(res.data);
    } catch {
      toast.error("Error cargando episodio");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchEpisode(); }, [fetchEpisode]);

  const handleSaveEval = async (e) => {
    e.preventDefault();
    const v = validate(evaluationSchema, evalForm);
    if (!v.ok) {
      setErrors(v.errors);
      toast.error(Object.values(v.errors)[0]);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      if (editingEval) {
        await evaluationsAPI.update(editingEval, evalForm);
        toast.success("Evaluacion actualizada");
      } else {
        await evaluationsAPI.create(id, evalForm);
        toast.success("Evaluacion creada");
      }
      setShowEvalDialog(false);
      setEditingEval(null);
      setEvalForm(EMPTY_EVAL);
      fetchEpisode();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    const v = validate(sessionNoteSchema, noteForm);
    if (!v.ok) {
      setErrors(v.errors);
      toast.error(Object.values(v.errors)[0]);
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await sessionNotesAPI.create(id, noteForm);
      toast.success("Nota de sesion creada");
      setShowNoteDialog(false);
      setNoteForm(EMPTY_NOTE);
      fetchEpisode();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExPlan = async (e) => {
    e.preventDefault();
    const validExercises = exercises.filter((ex) => ex.name.trim());
    const v = validate(exercisePlanSchema, { title: exTitle, description: exDesc, exercises: validExercises });
    if (!v.ok) {
      setErrors(v.errors);
      toast.error(Object.values(v.errors)[0]);
      return;
    }
    setErrors({});
    if (!exTitle.trim()) return toast.error("El titulo es requerido");
    setSaving(true);
    try {
      await exercisePlansAPI.create({
        episode_id: id,
        patient_id: episode.patient_id,
        title: exTitle,
        description: exDesc,
        exercises: exercises.filter((ex) => ex.name.trim()),
      });
      toast.success("Plan de ejercicios creado");
      setShowExDialog(false);
      setExTitle("");
      setExDesc("");
      setExercises([{ ...EMPTY_EXERCISE }]);
      fetchEpisode();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const closeEpisode = async () => {
    try {
      await episodesAPI.close(id);
      toast.success("Episodio cerrado");
      fetchEpisode();
    } catch {
      toast.error("Error al cerrar episodio");
    }
  };

  const openEditEval = (ev) => {
    setEditingEval(ev.id);
    setEvalForm({
      subjective: ev.subjective || "", objective: ev.objective || "",
      assessment: ev.assessment || "", plan: ev.plan || "",
      pain_level: ev.pain_level || 5, rom_notes: ev.rom_notes || "",
      strength_notes: ev.strength_notes || "", functional_notes: ev.functional_notes || "",
      goals: ev.goals || "", frequency: ev.frequency || "",
    });
    setShowEvalDialog(true);
  };

  const addExercise = () => setExercises([...exercises, { ...EMPTY_EXERCISE }]);
  const removeExercise = (i) => setExercises(exercises.filter((_, idx) => idx !== i));
  const updateExercise = (i, field, val) => {
    const copy = [...exercises];
    copy[i] = { ...copy[i], [field]: val };
    setExercises(copy);
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-32 bg-muted rounded-xl animate-pulse" /><div className="h-64 bg-muted rounded-xl animate-pulse" /></div>;
  }
  if (!episode) {
    return <div className="text-center py-16"><p className="text-muted-foreground">Episodio no encontrado</p><Button variant="link" onClick={() => navigate(-1)}>Volver</Button></div>;
  }

  const evals = episode.evaluations || [];
  const notes = episode.session_notes || [];
  const plans = episode.exercise_plans || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="episode-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" className="mt-1" onClick={() => navigate(`/pacientes/${episode.patient_id}`)} data-testid="back-to-patient">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">{episode.title}</h1>
            <Badge variant={episode.status === "active" ? "default" : "secondary"}>
              {episode.status === "active" ? "Activo" : "Completado"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Paciente: {episode.patient?.name || "—"} &middot; Inicio: {episode.start_date?.split("T")[0]}
          </p>
          {episode.chief_complaint && <p className="text-sm text-muted-foreground mt-1">Motivo: {episode.chief_complaint}</p>}
        </div>
        {episode.status === "active" && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={closeEpisode} data-testid="close-episode-btn">
            <CheckCircle className="w-4 h-4 mr-1.5" />Cerrar episodio
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="evaluation" className="w-full">
        <TabsList className="w-full sm:w-auto flex-wrap">
          <TabsTrigger value="evaluation" data-testid="tab-evaluation"><ClipboardList className="w-3.5 h-3.5 mr-1.5" />Evaluacion</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes"><FileText className="w-3.5 h-3.5 mr-1.5" />Notas ({notes.length})</TabsTrigger>
          <TabsTrigger value="exercises" data-testid="tab-exercises"><Dumbbell className="w-3.5 h-3.5 mr-1.5" />Ejercicios ({plans.length})</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Progreso</TabsTrigger>
        </TabsList>

        {/* Evaluation Tab */}
        <TabsContent value="evaluation" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button size="sm" className="rounded-full" onClick={() => { setEditingEval(null); setEvalForm(EMPTY_EVAL); setShowEvalDialog(true); }} data-testid="new-eval-btn">
              <Plus className="w-4 h-4 mr-1.5" />{evals.length > 0 ? "Agregar evaluacion" : "Crear evaluacion inicial"}
            </Button>
          </div>
          {evals.length > 0 ? (
            evals.map((ev) => (
              <Card key={ev.id} data-testid={`eval-card-${ev.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Evaluacion &middot; {ev.created_at?.split("T")[0]}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => openEditEval(ev)} data-testid={`edit-eval-${ev.id}`}>Editar</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <EvalField label="Subjetivo" value={ev.subjective} />
                  <EvalField label="Objetivo" value={ev.objective} />
                  <EvalField label="Evaluacion" value={ev.assessment} />
                  <EvalField label="Plan" value={ev.plan} />
                  <div className="flex items-center gap-3 flex-wrap">
                    <PainBadge level={ev.pain_level} />
                    {ev.frequency && <Badge variant="outline" className="text-xs">Frecuencia: {ev.frequency}</Badge>}
                  </div>
                  {ev.goals && <EvalField label="Objetivos" value={ev.goals} />}
                </CardContent>
              </Card>
            ))
          ) : (
            <EmptyState icon={ClipboardList} text="Sin evaluacion inicial" />
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button size="sm" className="rounded-full" onClick={() => { setNoteForm(EMPTY_NOTE); setShowNoteDialog(true); }} data-testid="new-note-btn">
              <Plus className="w-4 h-4 mr-1.5" />Nueva nota de sesion
            </Button>
          </div>
          {notes.length > 0 ? (
            <div className="relative pl-8 space-y-4">
              {notes.map((n) => (
                <div key={n.id} className="relative timeline-item" data-testid={`note-card-${n.id}`}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Sesion &middot; {n.created_at?.split("T")[0]}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <EvalField label="Subjetivo" value={n.subjective} />
                      <EvalField label="Intervenciones" value={n.interventions} />
                      <EvalField label="Respuesta del paciente" value={n.patient_response} />
                      <EvalField label="Progreso" value={n.progress_notes} />
                      <EvalField label="Plan proxima sesion" value={n.next_session_plan} />
                      <PainBadge level={n.pain_level} />
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={FileText} text="Sin notas de sesion" />
          )}
        </TabsContent>

        {/* Exercises Tab */}
        <TabsContent value="exercises" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button size="sm" className="rounded-full" onClick={() => { setExTitle(""); setExDesc(""); setExercises([{ ...EMPTY_EXERCISE }]); setShowExDialog(true); }} data-testid="new-exercise-plan-btn">
              <Plus className="w-4 h-4 mr-1.5" />Nuevo plan de ejercicios
            </Button>
          </div>
          {plans.length > 0 ? (
            plans.map((plan) => (
              <Card key={plan.id} data-testid={`plan-card-${plan.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{plan.title}</CardTitle>
                    <Badge variant={plan.status === "active" ? "default" : "secondary"} className="text-[10px]">
                      {plan.status === "active" ? "Activo" : "Completado"}
                    </Badge>
                  </div>
                  {plan.description && <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {plan.exercises?.map((ex, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted/50 flex items-start gap-3">
                        <Dumbbell className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{ex.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {ex.sets} series x {ex.reps} reps &middot; {ex.frequency}
                            {ex.hold_seconds > 0 && ` \u00B7 ${ex.hold_seconds}s hold`}
                          </p>
                          {ex.description && <p className="text-xs text-muted-foreground mt-1">{ex.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <EmptyState icon={Dumbbell} text="Sin planes de ejercicios" />
          )}
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress" className="mt-4">
          {notes.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Evolucion del dolor</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {notes.slice().reverse().map((n, i) => (
                    <div key={n.id} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20">{n.created_at?.split("T")[0]}</span>
                      <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(n.pain_level || 0) * 10}%`,
                            backgroundColor: n.pain_level <= 3 ? "hsl(var(--success))" : n.pain_level <= 6 ? "hsl(var(--warning))" : "hsl(var(--destructive))",
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium w-6 text-right">{n.pain_level || 0}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <EmptyState icon={TrendingUp} text="Registra notas de sesion para ver el progreso" />
          )}
        </TabsContent>
      </Tabs>

      {/* Evaluation Dialog */}
      <Dialog open={showEvalDialog} onOpenChange={setShowEvalDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="eval-dialog">
          <DialogHeader>
            <DialogTitle>{editingEval ? "Editar evaluacion" : "Nueva evaluacion inicial"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEval} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Subjetivo (historia del paciente)</Label>
                <Textarea value={evalForm.subjective} onChange={(e) => setEvalForm({ ...evalForm, subjective: e.target.value })} rows={3} placeholder="Que refiere el paciente..." data-testid="eval-subjective" />
                {errors.subjective && <p className="text-xs text-destructive mt-1">{errors.subjective}</p>}
              </div>
              <div className="sm:col-span-2">
                <Label>Objetivo (hallazgos de exploracion)</Label>
                <Textarea value={evalForm.objective} onChange={(e) => setEvalForm({ ...evalForm, objective: e.target.value })} rows={3} placeholder="Hallazgos clinicos..." data-testid="eval-objective" />
                {errors.objective && <p className="text-xs text-destructive mt-1">{errors.objective}</p>}
              </div>
              <div>
                <Label>Notas de rango de movimiento</Label>
                <Textarea value={evalForm.rom_notes} onChange={(e) => setEvalForm({ ...evalForm, rom_notes: e.target.value })} rows={2} data-testid="eval-rom" />
              </div>
              <div>
                <Label>Notas de fuerza</Label>
                <Textarea value={evalForm.strength_notes} onChange={(e) => setEvalForm({ ...evalForm, strength_notes: e.target.value })} rows={2} data-testid="eval-strength" />
              </div>
              <div className="sm:col-span-2">
                <Label>Evaluacion / Diagnostico</Label>
                <Textarea value={evalForm.assessment} onChange={(e) => setEvalForm({ ...evalForm, assessment: e.target.value })} rows={2} placeholder="Diagnostico fisioterapeutico..." data-testid="eval-assessment" />
                {errors.assessment && <p className="text-xs text-destructive mt-1">{errors.assessment}</p>}
              </div>
              <div className="sm:col-span-2">
                <Label>Plan de tratamiento</Label>
                <Textarea value={evalForm.plan} onChange={(e) => setEvalForm({ ...evalForm, plan: e.target.value })} rows={2} placeholder="Plan terapeutico..." data-testid="eval-plan" />
                {errors.plan && <p className="text-xs text-destructive mt-1">{errors.plan}</p>}
              </div>
              <div>
                <Label>Objetivos terapeuticos</Label>
                <Textarea value={evalForm.goals} onChange={(e) => setEvalForm({ ...evalForm, goals: e.target.value })} rows={2} data-testid="eval-goals" />
              </div>
              <div>
                <Label>Frecuencia de sesiones</Label>
                <Input value={evalForm.frequency} onChange={(e) => setEvalForm({ ...evalForm, frequency: e.target.value })} placeholder="Ej: 2 veces/semana" data-testid="eval-frequency" />
              </div>
              <div className="sm:col-span-2">
                <Label>Nivel de dolor: {evalForm.pain_level}/10</Label>
                <Slider
                  value={[evalForm.pain_level]}
                  onValueChange={([v]) => setEvalForm({ ...evalForm, pain_level: v })}
                  min={0} max={10} step={1}
                  className="mt-2"
                  data-testid="eval-pain-slider"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                  <span>Sin dolor</span><span>Dolor maximo</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowEvalDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="eval-save-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Session Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>Nueva nota de sesion</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveNote} className="space-y-4">
            <div>
              <Label>Subjetivo</Label>
              <Textarea value={noteForm.subjective} onChange={(e) => setNoteForm({ ...noteForm, subjective: e.target.value })} rows={2} placeholder="Que refiere el paciente hoy..." data-testid="note-subjective" />
              {errors.subjective && <p className="text-xs text-destructive mt-1">{errors.subjective}</p>}
            </div>
            <div>
              <Label>Objetivo</Label>
              <Textarea value={noteForm.objective} onChange={(e) => setNoteForm({ ...noteForm, objective: e.target.value })} rows={2} placeholder="Hallazgos de la sesion..." data-testid="note-objective" />
            </div>
            <div>
              <Label>Intervenciones realizadas</Label>
              <Textarea value={noteForm.interventions} onChange={(e) => setNoteForm({ ...noteForm, interventions: e.target.value })} rows={2} placeholder="Tecnicas y tratamientos aplicados..." data-testid="note-interventions" />
              {errors.interventions && <p className="text-xs text-destructive mt-1">{errors.interventions}</p>}
            </div>
            <div>
              <Label>Respuesta del paciente</Label>
              <Textarea value={noteForm.patient_response} onChange={(e) => setNoteForm({ ...noteForm, patient_response: e.target.value })} rows={2} data-testid="note-response" />
            </div>
            <div>
              <Label>Notas de progreso</Label>
              <Textarea value={noteForm.progress_notes} onChange={(e) => setNoteForm({ ...noteForm, progress_notes: e.target.value })} rows={2} data-testid="note-progress" />
            </div>
            <div>
              <Label>Plan proxima sesion</Label>
              <Textarea value={noteForm.next_session_plan} onChange={(e) => setNoteForm({ ...noteForm, next_session_plan: e.target.value })} rows={2} data-testid="note-next-plan" />
            </div>
            <div>
              <Label>Nivel de dolor: {noteForm.pain_level}/10</Label>
              <Slider
                value={[noteForm.pain_level]}
                onValueChange={([v]) => setNoteForm({ ...noteForm, pain_level: v })}
                min={0} max={10} step={1}
                className="mt-2"
                data-testid="note-pain-slider"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowNoteDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="note-save-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? "Guardando..." : "Guardar nota"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Exercise Plan Dialog */}
      <Dialog open={showExDialog} onOpenChange={setShowExDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="exercise-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo plan de ejercicios</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveExPlan} className="space-y-4">
            <div>
              <Label>Titulo del plan *</Label>
              <Input value={exTitle} onChange={(e) => setExTitle(e.target.value)} placeholder="Ej: Ejercicios para hombro" required data-testid="ex-plan-title" />
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea value={exDesc} onChange={(e) => setExDesc(e.target.value)} rows={2} placeholder="Indicaciones generales..." data-testid="ex-plan-desc" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="mb-0">Ejercicios</Label>
                <Button type="button" variant="outline" size="sm" onClick={addExercise} data-testid="add-exercise-btn">
                  <Plus className="w-3 h-3 mr-1" />Agregar
                </Button>
              </div>
              {exercises.map((ex, i) => (
                <Card key={i} className="bg-muted/30">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Ejercicio {i + 1}</span>
                      {exercises.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeExercise(i)} data-testid={`remove-exercise-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <Input placeholder="Nombre del ejercicio" value={ex.name} onChange={(e) => updateExercise(i, "name", e.target.value)} data-testid={`ex-name-${i}`} />
                    <Textarea placeholder="Instrucciones" value={ex.description} onChange={(e) => updateExercise(i, "description", e.target.value)} rows={2} data-testid={`ex-desc-${i}`} />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Series</Label>
                        <Input type="number" value={ex.sets} onChange={(e) => updateExercise(i, "sets", Number(e.target.value))} data-testid={`ex-sets-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Reps</Label>
                        <Input type="number" value={ex.reps} onChange={(e) => updateExercise(i, "reps", Number(e.target.value))} data-testid={`ex-reps-${i}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Frecuencia</Label>
                        <Input value={ex.frequency} onChange={(e) => updateExercise(i, "frequency", e.target.value)} data-testid={`ex-freq-${i}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowExDialog(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-full" disabled={saving} data-testid="ex-plan-save-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? "Guardando..." : "Crear plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EvalField({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground whitespace-pre-wrap mt-0.5">{value}</p>
    </div>
  );
}

function PainBadge({ level }) {
  const color = level <= 3 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
    : level <= 6 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
  return <Badge variant="secondary" className={`text-[10px] ${color}`}>Dolor: {level || 0}/10</Badge>;
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 text-muted-foreground">
      <Icon className="w-10 h-10 mx-auto mb-2 opacity-30" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
